// Phase 8B.1 — SMS automation label/tone maps.
// Status tones map to the existing StatusPill primitive; do NOT introduce
// new colour tokens. Keep labels short and Title Case.

export const STATUS_TONES = {
  eligible: 'info',
  sending: 'info',
  sent: 'success',
  skipped: 'neutral',
  blocked: 'warning',
  failed: 'danger',
};

export const STATUS_LABELS = {
  eligible: 'Eligible',
  sending: 'Sending',
  sent: 'Sent',
  skipped: 'Skipped',
  blocked: 'Blocked',
  failed: 'Failed',
};

export const REASON_LABELS = {
  missed_call: 'Missed call',
  high_intent_no_booking: 'High intent — no booking',
  requested_human_backup: 'Requested human',
  appointment_confirmation: 'Appointment confirmation',
};

export const BLOCK_REASON_LABELS = {
  dnc: 'DNC list',
  opted_out: 'Opted out',
  frequency_cap: 'Frequency cap',
  quiet_hours: 'Quiet hours',
  rate_limited: 'Rate limited',
  duplicate: 'Duplicate',
  sms_automation_disabled: 'Automation disabled',
  trigger_disabled: 'Trigger disabled',
  call_in_progress: 'Call in progress',
  no_customer_phone: 'No phone',
};

// Filter dropdown options — keep the visible set narrow.
export const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'eligible', label: 'Eligible' },
  { value: 'sent', label: 'Sent' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'failed', label: 'Failed' },
];

export const REASON_FILTER_OPTIONS = [
  { value: '', label: 'All triggers' },
  { value: 'missed_call', label: REASON_LABELS.missed_call },
  { value: 'high_intent_no_booking', label: REASON_LABELS.high_intent_no_booking },
  { value: 'requested_human_backup', label: REASON_LABELS.requested_human_backup },
  { value: 'appointment_confirmation', label: REASON_LABELS.appointment_confirmation },
];

// Trigger toggle metadata — used by AutomationControls for the 2-col grid.
export const TRIGGER_FIELDS = [
  { key: 'missed_call', label: 'Missed call', helper: 'Customer hung up before connecting' },
  { key: 'high_intent_no_booking', label: 'High intent — no booking', helper: 'Strong interest but no appointment booked' },
  { key: 'requested_human_backup', label: 'Requested human', helper: 'Caller asked to speak with a person' },
  { key: 'appointment_confirmation', label: 'Appointment confirmation', helper: 'Outbound confirmation after booking' },
];

// Read-only mirror of `backend/services/sms_followup/templates.py`.
// Templates ship in code (Phase 8A constraint), so the Templates tab in
// the UI is a static reference — it never tries to fetch them.
// Tokens like {dealership_name} and {appointment_time} are kept verbatim
// so the operator can see exactly what gets substituted at send time.
export const TEMPLATE_DEFINITIONS = [
  {
    id: 'missed_call_v1',
    touch: 1,
    description:
      'Sent after an unanswered outbound attempt. Short, friendly, no pressure.',
    body:
      'Hi {first_name}, this is {dealership_name}. We tried calling — reply here or call back when convenient.',
  },
  {
    id: 'high_intent_no_booking_v1',
    touch: 1,
    description:
      'Sent when the call ended with high intent but no appointment was booked. Offers a single concrete next step.',
    body:
      'Hi {first_name}, thanks for chatting with {dealership_name}. Want a quick callback to lock in details?',
  },
  {
    id: 'requested_human_backup_v1',
    touch: 1,
    description:
      'Backup SMS for the customer_requested_human path — sent only when the agent did not transfer to a person.',
    body:
      'Hi {first_name}, {dealership_name} here — a team member will call you back shortly. Reply with a good time.',
  },
  {
    id: 'appointment_confirmation_v1',
    touch: 1,
    description:
      'Sent immediately after an appointment is created. Single confirmation text — no reminders here.',
    body:
      'Hi {first_name}, your visit with {dealership_name} is confirmed for {appointment_time}. Reply if you need to change it.',
  },
  {
    id: 'missed_call_followup_v1',
    touch: 2,
    description:
      'Second-touch SMS for an unanswered missed_call follow-up. Sent ~24h after the initial.',
    body:
      'Hi {first_name}, just checking in from {dealership_name}. Still happy to help when you have a minute.',
  },
  {
    id: 'high_intent_followup_v1',
    touch: 2,
    description:
      'Second-touch SMS for an unanswered high_intent_no_booking follow-up. Sent within 4-6h.',
    body:
      'Hi {first_name}, {dealership_name} again. Want me to set up a quick callback?',
  },
];
